import type {Component} from 'grapesjs';
import type {PrimaryConfigOptions} from '../index';
import type {FontSelectorType, FontData} from '../styles.types';

export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const styleManager = editor.StyleManager;

  function isFormMakerBackgroundComponent(selected: Component | null): boolean {
    return (
      typeof window.PMW.formmaker !== 'undefined' &&
      selected?.is(window.PMW.formmaker.components.structural.PMW_FORM_BACKGROUND) === true &&
      selected.parent()?.is(window.PMW.formmaker.components.structural.PMW_SIGNUP_FORM) === true
    );
  }

  styleManager.addType('pmw-font-selector', {
    create(data: any): HTMLDivElement {
      const {change} = data;
      const props = data.props as FontSelectorType;
      props.component = props.component ?? editor.getSelected();

      const selected = props.component;
      const styles = selected?.getStyle() ?? {};
      let currFont: string | null = (styles['font-family'] as string) ?? props.defaults?.value ?? null;
      let allComponentsWithFont: Component[] | null = null;

      if (selected && isFormMakerBackgroundComponent(selected)) {
        allComponentsWithFont = editor.runCommand(window.PMW.emails.commands.GET_ALL_COMPONENTS_WITH_FONT);
        if (allComponentsWithFont && allComponentsWithFont.length > 0) {
          currFont = areAllFontsSame(allComponentsWithFont);
        }
      }

      const [labelContainer, selectorContainer] = getFontSelectorContainers(props, currFont);
      attachFontSelectorHandlers(props, labelContainer, selectorContainer, allComponentsWithFont, change);

      return labelContainer;
    },

    emit(
      data: any,
      emitData: {
        labelContainer: HTMLElement;
        itemEl: HTMLElement;
        fontFamily: string;
        fontName: string;
        allComponentsWithFont: Component[] | null;
      }
    ): void {
      const props = data.props as FontSelectorType;
      const {labelContainer, itemEl, fontFamily, fontName, allComponentsWithFont} = emitData;
      const selected = props.component ?? editor.getSelected();

      if (selected && isFormMakerBackgroundComponent(selected) && allComponentsWithFont && allComponentsWithFont.length > 0) {
        allComponentsWithFont.map((component: Component): void => {
          editor.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {
            component: component,
            property: props.property,
            value: fontFamily,
          });
        });
      }

      $(itemEl).addClass('-selected').siblings('.js-fonts-list-item').removeClass('-selected');

      $(labelContainer).find('.js-font-label-btn-txt').html(fontName).css('font-family', fontFamily);

      editor.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {
        component: props.component,
        property: props.property,
        value: fontFamily,
      });
    },
  });

  function getFontSelectorContainers(props: FontSelectorType, currFont: string | null): [HTMLDivElement, HTMLDivElement] {
    const labelContainer = document.createElement('div');
    const selectorContainer = document.createElement('div');
    labelContainer.className = props.type;
    selectorContainer.className = props.type;

    let selectedFont: FontData | undefined;

    const infoContainer = `<div class="info-wrapper -warning">
                <div class="info-box">
                    <div class="body-md-regular info-message">
                        ${window.i18next.t('pmwjs_fonts_email_warning')}
                    </div>
                </div>
            </div>`;

    const fontsList = `<ul class="fonts-list"> 
                ${props.list
                  .map((font: FontData): string => {
                    let selectedClass = '';
                    if (font.value === currFont) {
                      selectedFont = font;
                      selectedClass = '-selected';
                    }

                    return `<li class="fonts-list-item js-fonts-list-item ${selectedClass}">
                            <span class="fonts-list-item-text body-lg-regular js-fonts-list-item-text"
                                data-font-family="${font.value}" data-font-name="${font.name}" 
                                style="font-family: ${font.value}">${font.name}</span>
                            <i class="icon icon-check body-l"></i>
                        </li>`;
                  })
                  .join('')}
            </ul>`;

    const selectedFontName = selectedFont?.name ?? 'Mixed';

    const fontsListContainer = `<div class="fonts-list-container">
                <span class="fonts-list-title body-md-bold">${window.i18next.t('pmwjs_fonts')}</span>
                ${fontsList}
            </div>`;

    selectorContainer.innerHTML = `<div class="selector-container">
                ${infoContainer}
                ${fontsListContainer}
            </div>`;

    labelContainer.innerHTML = `<div class="label-container">
                <span class="style-manager-label">${props.label}</span>
                <button class="font-label-btn js-font-label-btn">
                    <span class="font-label-btn-txt body-lg-regular js-font-label-btn-txt">${selectedFontName}</span>
                </button>
            </div>`;

    return [labelContainer, selectorContainer];
  }

  function attachFontSelectorHandlers(
    _props: FontSelectorType,
    labelContainer: HTMLDivElement,
    selectorContainer: HTMLDivElement,
    allComponentsWithFont: Component[] | null,
    change: (...args: any) => void
  ): void {
    window.PMW.onClick($(labelContainer), '.js-font-label-btn', function (): void {
      editor.runCommand(window.PMW.emails.commands.OPEN_SIDE_PANEL, {
        title: window.i18next.t('pmwjs_font'),
        content: selectorContainer,
      });
    });

    selectorContainer.querySelectorAll('.js-fonts-list-item').forEach((itemEl: Element): void => {
      const textItem = itemEl.querySelector('.js-fonts-list-item-text') as HTMLElement;
      if (textItem) {
        const fontFamily = textItem.getAttribute('data-font-family') ?? '';
        const fontName = textItem.getAttribute('data-font-name') ?? '';

        itemEl.addEventListener('click', function (): void {
          change({labelContainer, itemEl, fontFamily, fontName, allComponentsWithFont});
        });
      }
    });
  }

  function areAllFontsSame(components: Component[]): string | null {
    let font: string | null = null;

    for (const item of components) {
      const componentFont = item.getStyle()['font-family'] as string;
      if (!font) {
        font = componentFont;
        continue;
      }

      if (componentFont !== font) {
        return null;
      }
    }

    return font;
  }
};
